import React, { useState } from 'react';
import { I18n } from '@aws-amplify/core';
import { Button, Divider, Input } from 'antd';

import { useCreateTLID } from '@/hooks/v1/useTLIDMutations';
import { useGetBeraReportV2 } from '@/hooks/useGetBeraReportV2';
import { useBeraReportV2 } from '@/context/BeraReportV2';
import { ModalDelete } from '@/components/ui/Modals/ModalDelete';
import { CheckCircleIcon, CloseCircleIcon, PlusCircleIcon, TrashIcon } from '@/assets/icons/v2';

import { Item } from './Item';
import {
	ActionButton,
	AddStretchButton,
	ConfirmButton,
	DeleteButton,
	FooterRow,
	ListContainer,
	TlidActions,
	TlidCreationRow,
	TlidRow,
	TlidSelect
} from '../styles';

const MIN_TLID_NAME_LENGTH = 3;
const EMPTY_FILE_NAME = '';
const NO_REPORT_ID = '';

export function List(): JSX.Element {
	const {
		organizationId,
		companyId,
		file,
		jobSummary,
		tasks,
		videoDuration,
		selectedReport,
		selectedTaskId,
		listedStretches,
		canAddStretch,
		isSaving,
		onAddStretch,
		onChangeStretch,
		onRemoveStretch,
		onSelectTask,
		onResetDraft,
		onClearSelection,
		onDiscardReport,
		onDropSelectedTaskStretches,
		onConfirmStretches,
		onRemoveStretches,
		onReportSaved
	} = useBeraReportV2();

	const [tlidName, setTlidName] = useState<string>('');
	const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);

	const { mutateAsync: createTlid, isLoading: creatingTlid } = useCreateTLID();

	const options = tasks.map((task) => ({ value: task.id, label: task.name }));

	const selectedReportId = selectedReport?.id;

	const { data: reportDetails } = useGetBeraReportV2({
		id: selectedReportId ?? NO_REPORT_ID,
		organization_id: organizationId,
		company_id: companyId
	});

	const isConsolidated = !!reportDetails?.consolidated;

	const isDeleteDisabled = !selectedReportId || isSaving || isConsolidated;

	const hasStretches = !!listedStretches.length;

	const canCreateTlid = tlidName.length >= MIN_TLID_NAME_LENGTH;

	const canConfirm = hasStretches && !!selectedTaskId;

	async function handleCreateTlid(): Promise<void> {
		const body = {
			organization_id: organizationId,
			company_id: companyId,
			name: tlidName,
			description: tlidName,
			cycle_id: jobSummary?.cycle_id
		};

		const created = await createTlid({ body });

		setTlidName('');

		if (created?.data?.id) {
			onSelectTask(created.data.id);
		}
	}

	function handleTlidNameChange({ target }: React.ChangeEvent<HTMLInputElement>): void {
		setTlidName(target.value);
	}

	function handleSelectTlid(value: unknown): void {
		onSelectTask(value as string);
	}

	function handleSaved(): void {
		onResetDraft();

		onReportSaved();
	}

	function handleDeleted(): void {
		handleCloseDeleteModal();

		onDropSelectedTaskStretches();

		onReportSaved();
	}

	function handleConfirm(): void {
		if (!selectedTaskId) {
			return;
		}

		const params = {
			taskId: selectedTaskId,
			reportId: selectedReportId,
			stretches: listedStretches,
			onSuccess: handleSaved
		};

		onConfirmStretches(params);
	}

	function handleCancel(): void {
		onResetDraft();

		onClearSelection();

		onDiscardReport();
	}

	function findStretchFileName(stretchId: string): string {
		const segment = selectedReport?.segments?.find((item) => item.id === stretchId);
		const result = segment?.source_file_name ?? file?.original_name ?? EMPTY_FILE_NAME;

		return result;
	}

	function handleOpenDeleteModal(): void {
		setIsDeleteModalOpen(true);
	}

	function handleCloseDeleteModal(): void {
		setIsDeleteModalOpen(false);
	}

	function handleConfirmDelete(): void {
		if (!selectedReportId) {
			return;
		}

		const params = { reportId: selectedReportId, onSuccess: handleDeleted };

		onRemoveStretches(params);
	}

	function renderWithCreation(menu: JSX.Element): JSX.Element {
		return (
			<div>
				{menu}
				<Divider style={{ margin: '4px 0' }} />
				<TlidCreationRow>
					<Input value={tlidName} onChange={handleTlidNameChange} />
					<Button type="link" loading={creatingTlid} disabled={!canCreateTlid} onClick={handleCreateTlid}>
						{I18n.get('Add item')}
					</Button>
				</TlidCreationRow>
			</div>
		);
	}

	return (
		<ListContainer>
			<TlidRow>
				<TlidSelect
					showSearch
					options={options}
					value={selectedTaskId}
					placeholder={I18n.get('Select a TLID')}
					dropdownRender={renderWithCreation}
					onChange={handleSelectTlid}
				/>
				<TlidActions>
					<DeleteButton
						icon={<TrashIcon />}
						disabled={isDeleteDisabled}
						aria-label={I18n.get('Delete TLID')}
						onClick={handleOpenDeleteModal}
					/>
					<ActionButton
						icon={<CloseCircleIcon />}
						disabled={isSaving}
						aria-label={I18n.get('Discard')}
						onClick={handleCancel}
					/>
					<ConfirmButton
						icon={<CheckCircleIcon />}
						loading={isSaving}
						disabled={!canConfirm}
						aria-label={I18n.get('Confirm')}
						onClick={handleConfirm}
					/>
				</TlidActions>
			</TlidRow>
			{listedStretches.map((stretch, index) => (
				<Item
					key={stretch.id}
					position={index + 1}
					videoDuration={videoDuration}
					fileName={findStretchFileName(stretch.id)}
					stretch={stretch}
					onChange={onChangeStretch}
					onRemove={onRemoveStretch}
				/>
			))}
			<FooterRow>
				<AddStretchButton
					type="link"
					disabled={!canAddStretch}
					icon={<PlusCircleIcon />}
					onClick={onAddStretch}
				>
					{I18n.get('Add stretch')}
				</AddStretchButton>
			</FooterRow>
			<ModalDelete
				open={isDeleteModalOpen}
				loading={isSaving}
				description={I18n.get('Heads up! Want to delete this TLID?')}
				onCancel={handleCloseDeleteModal}
				onConfirm={handleConfirmDelete}
			/>
		</ListContainer>
	);
}
